import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, AlertOctagon, FileText, Upload, ArrowLeft, QrCode, Printer, Image as ImageIcon, X } from "lucide-react";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

export default function AssetDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [inspOpen, setInspOpen] = useState(false);
  const [inspection, setInspection] = useState({ result: "pass", condition_rating: "Good", findings: "", recommendations: "", inspection_type: "Routine" });
  const [inspFiles, setInspFiles] = useState([]);
  const [defectOpen, setDefectOpen] = useState(false);
  const [defect, setDefect] = useState({ description: "", risk_level: "Medium" });
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/assets/${id}`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveInspection = async () => {
    try {
      const { data } = await api.post("/inspections", { asset_id: id, ...inspection });
      if (inspFiles.length > 0) {
        const fd = new FormData();
        inspFiles.forEach((f) => fd.append("files", f));
        try { await api.post(`/inspections/${data.id}/photos`, fd); }
        catch (e) { toast.error("Photos failed to upload"); }
      }
      toast.success("Inspection recorded");
      setInspOpen(false); setInspFiles([]);
      setInspection({ result: "pass", condition_rating: "Good", findings: "", recommendations: "", inspection_type: "Routine" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveDefect = async () => {
    try {
      await api.post("/defects", { asset_id: id, ...defect });
      toast.success("Defect logged"); setDefectOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const uploadDoc = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("related_type", "asset"); fd.append("related_id", id); fd.append("category", "photo");
    try { await api.post("/documents/upload", fd); toast.success("Uploaded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const a = data.asset;

  return (
    <div className="space-y-4" data-testid="asset-detail-page">
      <Link to="/assets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4"/>Back to assets</Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{a.asset_group}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{a.name}</h1>
          <div className="mt-2 flex gap-2">
            <Badge className="bg-slate-100 text-slate-700 border-0">{a.criticality} criticality</Badge>
            <Badge className="bg-emerald-100 text-emerald-800 border-0">{a.operational_status || "Operational"}</Badge>
            {a.location && <Badge variant="outline">{a.location}</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          <Dialog open={inspOpen} onOpenChange={setInspOpen}>
            <DialogTrigger asChild><Button data-testid="add-inspection-btn"><ClipboardCheck className="w-4 h-4 mr-1"/>Inspection</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New inspection</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Type</Label><Input value={inspection.inspection_type} onChange={(e)=>setInspection({...inspection, inspection_type:e.target.value})}/></div>
                <div><Label>Result</Label>
                  <Select value={inspection.result} onValueChange={(v)=>setInspection({...inspection, result:v})}>
                    <SelectTrigger data-testid="inspection-result"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pass">Pass</SelectItem>
                      <SelectItem value="attention_required">Attention required</SelectItem>
                      <SelectItem value="fail">Fail (creates a defect)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Condition</Label>
                  <Select value={inspection.condition_rating} onValueChange={(v)=>setInspection({...inspection, condition_rating:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["Excellent","Good","Fair","Poor","Unserviceable"].map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Findings</Label><Textarea value={inspection.findings} onChange={(e)=>setInspection({...inspection, findings:e.target.value})}/></div>
                <div><Label>Recommendations</Label><Textarea value={inspection.recommendations} onChange={(e)=>setInspection({...inspection, recommendations:e.target.value})}/></div>
                <div>
                  <Label>Photos (optional)</Label>
                  <input type="file" multiple accept="image/*" capture="environment"
                    onChange={(e)=>setInspFiles(Array.from(e.target.files||[]))}
                    className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-200 file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                    data-testid="inspection-photos"/>
                  {inspFiles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {inspFiles.map((f, i) => (
                        <div key={`${f.name}-${f.size}-${i}`} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3"/><span className="truncate max-w-[10rem]">{f.name}</span>
                          <button type="button" className="text-slate-400 hover:text-slate-700" onClick={()=>setInspFiles(inspFiles.filter((_,j)=>j!==i))}><X className="w-3 h-3"/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter><Button onClick={saveInspection} data-testid="inspection-save">Save inspection</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={defectOpen} onOpenChange={setDefectOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-defect-btn"><AlertOctagon className="w-4 h-4 mr-1"/>Defect</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Raise a defect</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Description</Label><Textarea value={defect.description} onChange={(e)=>setDefect({...defect, description:e.target.value})} data-testid="defect-description"/></div>
                <div><Label>Risk level</Label>
                  <Select value={defect.risk_level} onValueChange={(v)=>setDefect({...defect, risk_level:v})}>
                    <SelectTrigger data-testid="defect-risk"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["Low","Medium","High","Critical"].map((r)=><SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={saveDefect} disabled={!defect.description} data-testid="defect-save">Save defect</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={(e)=>e.target.files?.[0] && uploadDoc(e.target.files[0])} data-testid="asset-upload-input"/>
            <span className="inline-flex items-center h-9 px-3 rounded-md border border-slate-200 text-sm hover:bg-slate-50"><Upload className="w-4 h-4 mr-1"/>Upload</span>
          </label>

          <Dialog open={qrOpen} onOpenChange={setQrOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="asset-qr-btn"><QrCode className="w-4 h-4 mr-1"/>QR</Button></DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Asset QR code</DialogTitle></DialogHeader>
              <div className="flex flex-col items-center gap-3 pt-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/${id}/qr.png`} alt="QR code" className="w-64 h-64 border border-slate-200 rounded-md bg-white" data-testid="asset-qr-image"/>
                <div className="text-xs text-slate-500 text-center">Scan to open this asset on a phone and record an inspection.</div>
                <div className="text-[11px] font-mono text-slate-400 break-all text-center">{`/assets/${id}`}</div>
                <div className="flex gap-2 w-full">
                  <Button className="flex-1" variant="outline" onClick={()=>window.open(`${process.env.REACT_APP_BACKEND_URL}/api/assets/${id}/qr.png`, "_blank")} data-testid="asset-qr-download"><Printer className="w-4 h-4 mr-1"/>Open / print</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Location" value={a.location}/>
          <Field label="Quantity" value={a.quantity}/>
          <Field label="Ownership" value={a.ownership_status}/>
          <Field label="Service frequency" value={a.service_frequency}/>
          <Field label="Compliance / reference" value={a.compliance_ref}/>
          <Field label="Responsible party" value={a.responsible_party}/>
          <Field label="Record required" value={a.record_required}/>
          <Field label="Next service" value={a.next_service_date ? new Date(a.next_service_date).toLocaleDateString() : "—"}/>
          <div className="col-span-full">
            <Field label="Primary maintenance action" value={a.primary_action || a.description}/>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="inspections">
        <TabsList>
          <TabsTrigger value="inspections" data-testid="tab-inspections">Inspections ({data.inspections.length})</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance ({data.maintenance_tasks.length})</TabsTrigger>
          <TabsTrigger value="defects" data-testid="tab-defects">Defects ({data.defects.length})</TabsTrigger>
          <TabsTrigger value="work-orders" data-testid="tab-work-orders">Work Orders ({data.work_orders.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({data.documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="inspections">
          <Card className="border-slate-200"><CardContent className="p-4">
            {data.inspections.length === 0 ? <div className="text-sm text-slate-500 p-4">No inspections recorded.</div> : (
              <ul className="divide-y divide-slate-100">{data.inspections.map((i)=>(
                <li key={i.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{i.inspection_type} · <span className={i.result==="fail"?"text-red-600":i.result==="attention_required"?"text-amber-600":"text-emerald-600"}>{i.result}</span></div>
                      <div className="text-xs text-slate-500">{new Date(i.inspection_date).toLocaleString()} · {i.inspector_name}</div>
                      {i.findings && <div className="text-sm text-slate-700 mt-1">{i.findings}</div>}
                    </div>
                    {i.photo_ids?.length ? <Badge variant="outline" className="text-xs"><ImageIcon className="w-3 h-3 mr-1"/>{i.photo_ids.length} photo{i.photo_ids.length>1?"s":""}</Badge> : null}
                  </div>
                  {i.photo_ids?.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {i.photo_ids.map((pid)=>(
                        <a key={pid} href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${pid}/download`} target="_blank" rel="noreferrer">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${pid}/download`} alt="Inspection photo" className="w-16 h-16 object-cover rounded border border-slate-200 bg-slate-50" data-testid={`insp-photo-${pid}`}/>
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              ))}</ul>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="maintenance">
          <Card className="border-slate-200"><CardContent className="p-4">
            {data.maintenance_tasks.map((t)=>(
              <div key={t.id} className="py-3 border-b border-slate-100 last:border-0">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{t.title}</div>
                  <Badge className={t.status==="Overdue"?"bg-red-100 text-red-800 border-0":t.status==="Due Soon"?"bg-amber-100 text-amber-800 border-0":"bg-slate-100 text-slate-700 border-0"}>{t.status}</Badge>
                </div>
                <div className="text-xs text-slate-500">Due: {t.next_due_date ? new Date(t.next_due_date).toLocaleDateString() : "—"} · {t.frequency_text}</div>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="defects">
          <Card className="border-slate-200"><CardContent className="p-4">
            {data.defects.length === 0 ? <div className="text-sm text-slate-500 p-4">No defects.</div> :
              data.defects.map((d)=>(
                <div key={d.id} className="py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">{d.description}</div>
                    <Badge className={d.risk_level==="Critical"||d.risk_level==="High"?"bg-red-100 text-red-800 border-0":"bg-amber-100 text-amber-800 border-0"}>{d.risk_level}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.status} · Raised {new Date(d.date_identified).toLocaleDateString()}</div>
                </div>
              ))
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="work-orders">
          <Card className="border-slate-200"><CardContent className="p-4">
            {data.work_orders.length === 0 ? <div className="text-sm text-slate-500 p-4">No work orders.</div> :
              data.work_orders.map((w)=>(
                <div key={w.id} className="py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs">{w.wo_number}</div>
                    <Badge>{w.status}</Badge>
                  </div>
                  <div className="text-sm mt-1">{w.description}</div>
                </div>
              ))
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="border-slate-200"><CardContent className="p-4">
            {data.documents.length === 0 ? <div className="text-sm text-slate-500 p-4">No documents.</div> :
              <ul className="divide-y divide-slate-100">{data.documents.map((d)=>(
                <li key={d.id} className="py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-slate-400"/>
                    <span>{d.original_filename}</span>
                    {d.category && <Badge variant="outline" className="ml-2 text-xs">{d.category}</Badge>}
                  </div>
                  <a className="text-xs text-blue-600 hover:underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer">Open</a>
                </li>
              ))}</ul>
            }
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Input(props) {
  return <input {...props} className={`h-9 px-3 rounded-md border border-slate-200 w-full text-sm ${props.className||""}`}/>;
}
